import { useState, useCallback } from "react";
import { isRight } from "core/types/Either";
import { useWalletFeatureFlag } from "core/hooks/useWalletFeatureFlag";
import { useWalletAnalytics } from "core/hooks/useWalletAnalytics";
import { isMobileScreen } from "core/helpers/platformDetection";
import downloadBlob from "core/functions/downloadBlob";
import { WalletPassParams } from "domain/entity/WalletPass";
import di from "di/di";

export interface UseAddToWalletState {
  isEnabled: boolean;
  isLoadingFlag: boolean;
  isLoadingApple: boolean;
  isLoadingGoogle: boolean;
  errorMessage: string | null;
  isMobile: boolean;
  appleQRUrl: string | null;
  googleQRUrl: string | null;
  isAppleModalOpen: boolean;
  isGoogleModalOpen: boolean;
}

export interface UseAddToWalletActions {
  addToAppleWallet: (params: WalletPassParams) => Promise<void>;
  addToGoogleWallet: (params: WalletPassParams) => Promise<void>;
  openAppleModal: () => void;
  closeAppleModal: () => void;
  openGoogleModal: () => void;
  closeGoogleModal: () => void;
  clearError: () => void;
}

export type UseAddToWalletResult = UseAddToWalletState & UseAddToWalletActions;

export function useAddToWallet(): UseAddToWalletResult {
  const {
    enabled: isEnabled,
    isLoading: isLoadingFlag,
  } = useWalletFeatureFlag();
  const analytics = useWalletAnalytics();
  const isMobile = isMobileScreen();

  const [isLoadingApple, setIsLoadingApple] = useState(false);
  const [isLoadingGoogle, setIsLoadingGoogle] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [appleQRUrl, setAppleQRUrl] = useState<string | null>(null);
  const [googleQRUrl, setGoogleQRUrl] = useState<string | null>(null);
  const [isAppleModalOpen, setIsAppleModalOpen] = useState(false);
  const [isGoogleModalOpen, setIsGoogleModalOpen] = useState(false);

  const addToAppleWallet = useCallback(
    async (params: WalletPassParams): Promise<void> => {
      setIsLoadingApple(true);
      setErrorMessage(null);
      analytics.trackWalletAddAttempted({ planType: params.productType });

      const result = await di.getAppleWalletPass.execute(params);

      setIsLoadingApple(false);

      if (isRight(result)) {
        analytics.trackWalletAddSucceeded({
          planType: params.productType,
          correlationId: result.value.correlationId,
        });
        if (isMobile) {
          downloadBlob(result.value.blob, "pass.pkpass");
        } else {
          const blobUrl = URL.createObjectURL(result.value.blob);
          setAppleQRUrl(blobUrl);
          setIsAppleModalOpen(true);
        }
      } else {
        analytics.trackWalletAddFailed({ planType: params.productType });
        setErrorMessage(
          "Sorry, we could not add this card to Apple Wallet. Please try again later."
        );
      }
    },
    [analytics, isMobile]
  );

  const addToGoogleWallet = useCallback(
    async (params: WalletPassParams): Promise<void> => {
      setIsLoadingGoogle(true);
      setErrorMessage(null);
      analytics.trackWalletAddAttempted({ planType: params.productType });

      const result = await di.getGoogleWalletPass.execute(params);

      setIsLoadingGoogle(false);

      if (isRight(result)) {
        analytics.trackWalletAddSucceeded({
          planType: params.productType,
          correlationId: result.value.correlationId,
        });
        if (isMobile) {
          window.location.href = result.value.url;
        } else {
          setGoogleQRUrl(result.value.url);
          setIsGoogleModalOpen(true);
        }
      } else {
        analytics.trackWalletAddFailed({ planType: params.productType });
        setErrorMessage(
          "Sorry, we could not add this card to Google Wallet. Please try again later."
        );
      }
    },
    [analytics, isMobile]
  );

  const openAppleModal = useCallback(() => setIsAppleModalOpen(true), []);
  const closeAppleModal = useCallback(() => setIsAppleModalOpen(false), []);
  const openGoogleModal = useCallback(() => setIsGoogleModalOpen(true), []);
  const closeGoogleModal = useCallback(() => setIsGoogleModalOpen(false), []);
  const clearError = useCallback(() => setErrorMessage(null), []);

  return {
    isEnabled,
    isLoadingFlag,
    isLoadingApple,
    isLoadingGoogle,
    errorMessage,
    isMobile,
    appleQRUrl,
    googleQRUrl,
    isAppleModalOpen,
    isGoogleModalOpen,
    addToAppleWallet,
    addToGoogleWallet,
    openAppleModal,
    closeAppleModal,
    openGoogleModal,
    closeGoogleModal,
    clearError,
  };
}
